import { useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import GlobalStyle from '@elementary/common-ui/style/global';
import theme from '@elementary/common-ui/style/theme';
import {
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider,
} from '@mui/material';
import {
  QueryClient,
  QueryClientProvider,
  MutationCache,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RecoilRoot, useSetRecoilState } from 'recoil';
import 'shared/services/i18n';
import {
  SIGNATURE_EXPIRED_ERROR_CODE,
  getElementaryErrorCode,
  getPermissionErrorMessage,
} from 'shared/utils/permissionErrors';
import { snackbar } from './shared/store/atoms';

const QueryClientProviderWithPermissionHandling = ({
  children,
  disableCaching,
}: {
  children: React.ReactNode;
  disableCaching?: boolean;
}) => {
  const setSnackbar = useSetRecoilState(snackbar);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        mutationCache: new MutationCache({
          onError: (error) => {
            const errorMessage = getPermissionErrorMessage(error);
            if (errorMessage !== null) {
              setSnackbar((prev) => ({
                ...prev,
                open: true,
                variant: 'error',
                message: errorMessage,
                autoHide: false,
              }));
            }
            // Component-level onError handlers will still be called after this
          },
        }),
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              const elementaryErrorCode = getElementaryErrorCode(error);
              const isSignatureExpired =
                elementaryErrorCode === SIGNATURE_EXPIRED_ERROR_CODE;
              return (
                failureCount <= 3 &&
                (error as any).response?.status === 400 &&
                isSignatureExpired
              );
            },
            retryDelay: 3000,
            staleTime: disableCaching ? 0 : Infinity,
          },
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableCaching],
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} position="bottom-left" />
    </QueryClientProvider>
  );
};

const AppProviders = ({
  children,
  disableCaching,
}: {
  children: React.ReactNode;
  disableCaching?: boolean;
}) => (
  <RecoilRoot>
    <QueryClientProviderWithPermissionHandling disableCaching={disableCaching}>
      <StyledEngineProvider injectFirst>
        <MuiThemeProvider theme={theme}>
          <ThemeProvider theme={theme}>
            <GlobalStyle />
            {children}
          </ThemeProvider>
        </MuiThemeProvider>
      </StyledEngineProvider>
    </QueryClientProviderWithPermissionHandling>
  </RecoilRoot>
);

export default AppProviders;
